import { Box, CardMedia, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { addToRefsGeneric, renderCanvas } from "../../utils/funtions";
import logo from "../../assets/logo.png";

gsap.registerPlugin(ScrollTrigger);

// ---- Hook nativo para media query (sin MUI) ----
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia === "undefined"
    )
      return;
    const mql = window.matchMedia(query);

    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // estado inicial
    setMatches(mql.matches);

    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange as any);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange as any);
    };
  }, [query]);

  return matches;
}

// móvil = ancho pequeño o input táctil sin hover
const MOBILE_QUERY = "(max-width: 800px), (pointer: coarse) and (hover: none)";

type CanvasAnimatedProps = {
  frameCount: number;
  file: string;
  title?: string;
  firstSubtitle?: string;
  secondSubtitle?: string;
  featured?: string;
  lastText?: string;
  bgColor: string;
  showLogo?: boolean;
};

export const CanvasAnimated = ({
  frameCount,
  file,
  title,
  firstSubtitle,
  secondSubtitle,
  featured,
  lastText,
  bgColor,
  showLogo,
}: CanvasAnimatedProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const getFrameSrc = useCallback(
    (i: number) => `/frames/${file}/frame_${String(i).padStart(6, "0")}.webp`,
    [file]
  );

  const pinRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef({ frame: 0 });
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const lastIndexRef = useRef(-1);
  const scaleRef = useRef({ value: 1 });

  const containerTextRef = useRef<HTMLDivElement>(null);
  const contentTextRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement[]>([]);
  const addTitleRef = addToRefsGeneric(textRef);
  const logoRef = useRef<HTMLDivElement>(null);

  // --------- Precarga de frames (DESKTOP SOLAMENTE) ----------
  useEffect(() => {
    if (isMobile) {
      // En móvil no cargamos imágenes ni dibujamos en canvas
      imagesRef.current = [];
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx && canvasRef.current) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const images: HTMLImageElement[] = [];

    for (let i = 1; i <= frameCount; i++) {
      const img = new Image();
      // Sugerencias de rendimiento
      (img as any).decoding = "async";
      img.src = getFrameSrc(i);

      if (i === 1) {
        img.onload = () => {
          imagesRef.current[0] = img;
          renderCanvas(0, canvasRef, imagesRef, scaleRef);
        };
      }

      images.push(img);
    }

    imagesRef.current = images;

    return () => {
      imagesRef.current = [];
    };
  }, [isMobile, frameCount, getFrameSrc]);

  // --------- GSAP/ScrollTrigger ----------
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Logo
      if (showLogo && logoRef.current) {
        gsap.fromTo(
          logoRef.current,
          { scale: 0.8, opacity: 0, transformPerspective: 1000, rotateX: 0.01 },
          {
            scale: 1,
            opacity: 1,
            ease: "none",
            scrollTrigger: {
              trigger: logoRef.current,
              start: "top top-=400",
              end: "+=200",
              scrub: true,
            },
          }
        );
      }

      // Textos
      if (textRef.current?.length) {
        gsap.fromTo(
          textRef.current,
          { opacity: 0, y: 50, transformPerspective: 1000, rotateX: 0.01 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "none",
            scrollTrigger: {
              trigger: textRef.current,
              start: "top top+=200",
              end: "+=200",
              scrub: true,
            },
          }
        );
      }

      // BG color del contenedor
      if (containerTextRef.current && pinRef.current) {
        gsap.to(containerTextRef.current, {
          ease: "power1",
          backgroundColor: bgColor,
          scrollTrigger: {
            trigger: pinRef.current,
            start: "center center-=150",
            end: "+=100%",
            scrub: true,
          },
        });
      }

      if (contentTextRef.current && pinRef.current) {
        gsap.fromTo(
          contentTextRef.current,
          { scale: 0.5, transformPerspective: 1000, rotateX: 0.01 },
          {
            scale: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: pinRef.current,
              start: "center center",
              end: "+=100%",
              scrub: true,
            },
          }
        );
      }

      // En móvil NO creamos timelines que rendericen frames/canvas
      if (!isMobile && pinRef.current) {
        gsap.to(scaleRef.current, {
          value: 1.1,
          ease: "none",
          scrollTrigger: {
            trigger: pinRef.current,
            start: "center center",
            end: "+=100%",
            scrub: true,
          },
          onUpdate: () => {
            const index = Math.floor(frameRef.current.frame);
            renderCanvas(index, canvasRef, imagesRef, scaleRef);
          },
        });

        gsap.to(frameRef.current, {
          frame: frameCount - 1,
          snap: "frame",
          ease: "none",
          scrollTrigger: {
            trigger: pinRef.current,
            start: "top top",
            end: "+=100%",
            scrub: true,
          },
          onUpdate: () => {
            const index = Math.floor(frameRef.current.frame);
            if (lastIndexRef.current !== index) {
              renderCanvas(index, canvasRef, imagesRef, scaleRef);
              lastIndexRef.current = index;
            }
          },
        });
      }
    }, pinRef);

    return () => ctx.revert();
  }, [isMobile, frameCount, bgColor, showLogo]);

  return (
    <Box
      ref={pinRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100svh",
        backgroundColor: isMobile ? bgColor : "transparent",
      }}
    >
      <canvas
        ref={canvasRef}
        width={1980}
        height={1114}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <Box
        ref={containerTextRef}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          flexDirection: "column",
          position: "absolute",
          left: 0,
          top: 0,
          pointerEvents: "none",
          width: "100%",
          height: "100%",
        }}
      >
        <Box ref={contentTextRef}>
          <Typography
            ref={addTitleRef}
            sx={{
              fontSize: { xs: 13, lg: 25 },
              fontWeight: 600,
              background: "linear-gradient(90deg, #f8d8cb,#dd7247)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              paddingInline: 8,
            }}
          >
            {title}
          </Typography>

          <Typography
            ref={addTitleRef}
            sx={{
              fontSize: { xs: 15, lg: 35 },
              fontWeight: 600,
              color: theme.palette.background.paper,
              paddingInline: 8,
            }}
          >
            {firstSubtitle}
          </Typography>

          <Typography
            ref={addTitleRef}
            sx={{
              fontSize: { xs: 15, lg: 35 },
              fontWeight: 600,
              color: theme.palette.background.paper,
              paddingInline: 8,
            }}
          >
            {secondSubtitle}
            <span style={{ textShadow: "0px 0px 35px white", fontWeight: 800 }}>
              {" "}
              {featured}{" "}
            </span>
            {lastText}
          </Typography>
        </Box>
        {showLogo && (
          <Box
            ref={logoRef}
            display="flex"
            width="100%"
            justifyContent="center"
            height="100%"
            alignItems="center"
            position="relative"
          >
            <Box position="absolute" height={200}>
              <CardMedia
                component="img"
                image={logo}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Box
              position="absolute"
              width="100%"
              bottom={0}
              boxShadow="0px 0px 50px 60px white"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
